import React, { FC, PropsWithChildren, ReactElement } from "react";
import classes from './card.module.scss'
import { DataList } from "../../../../shared/ui/dataList/DataList";
import { IStudentData } from "../../model/types";
import { Link } from "react-router-dom";
import { Copy } from "../../../../shared/ui/copy";
import { useAppSelector } from "../../../../app/store/store";
import { FEATURES, FEATURE_SECTIONS, hasFeature } from "../../../my";
import { SanctionSequenceReset } from "../../../../features/sanctionSequenceReset";
import { studentService } from "../../api/StudentService";

interface IProps {
    student: IStudentData;
    paymentChange?: ReactElement;
    balanceChange?: ReactElement;
    hideStudentData?: boolean;
    hideTitles?: boolean;
}

export const StudentCard: FC<IProps & PropsWithChildren> = (
    {student, paymentChange, balanceChange, hideStudentData=false, hideTitles=false}
) => {

    const {my} = useAppSelector(s => s.myReducer)
    const targetUserId = Number(student.user_id ?? 0)
    const canReadStudentFinance = hasFeature(my, FEATURES.STUDENTS.CARD_FINANCE_READ, targetUserId)
    const canUpdateStudentPayment = hasFeature(my, FEATURES.STUDENTS.CARD_PAYMENT_UPDATE, targetUserId)
    const canUpdateStudentWallet = hasFeature(my, FEATURES.STUDENTS.CARD_WALLET_UPDATE, targetUserId)
    const canCopyPaymentLink = hasFeature(my, FEATURES.STUDENTS.CARD_PAYMENT_LINK_COPY, targetUserId)
    const canResetSanctionSequence = hasFeature(my, FEATURES.STUDENTS.SANCTION_SEQUENCE_RESET, targetUserId)
    const canReadTutor = hasFeature(my, FEATURE_SECTIONS.STAFF)
        && hasFeature(my, FEATURES.IDM.STAFF_READ, targetUserId)
    const tutorLink = student.user_id && student.user_id > 0
        ? `/staff/${student.user_id}`
        : ''

    const hasText = (value?: string | null) => Boolean(value?.trim())
    const hasValidDate = (value?: string | null) => {
        const normalizedValue = value?.trim() ?? ''
        return Boolean(normalizedValue) && !normalizedValue.startsWith('0001-01-01')
    }
    const hasVisibleMoney = (value?: string | null) => {
        const normalizedValue = value?.trim() ?? ''
        return Boolean(normalizedValue) && normalizedValue !== '0'
    }
    const hasWalletBalance = (value?: string | null) => Boolean(value?.trim())
    const hasVisiblePayment = (value?: string | null) => {
        const normalizedValue = value?.trim() ?? ''
        return Boolean(normalizedValue) && normalizedValue !== 'Неизвестная платежка'
    }

    const financeList: (ReactElement | string)[] = []

    if((canReadStudentFinance && hasWalletBalance(student.balance)) || canUpdateStudentWallet){
        financeList.push(
            <section className={classes.financeItem}>Кошелек: {hasWalletBalance(student.balance) ? student.balance : '0'} ₽ {canUpdateStudentWallet ? balanceChange : null}</section>
        )
    }

    if((canReadStudentFinance && hasVisiblePayment(student.payment_name)) || canUpdateStudentPayment){
        const paymentName = hasVisiblePayment(student.payment_name) ? student.payment_name : 'Не задана'
        financeList.push(
            <section className={classes.financeItem}>Платежка: {paymentName} {canUpdateStudentPayment ? paymentChange : null}</section>
        )
    }

    if(student.payment_url && canCopyPaymentLink){
        financeList.push(
            <section className={classes.payment}>
                Ссылка на оплату: 
                <Copy text={student.payment_url} />
            </section>
        )
    }

    const studentDataList = ([
        `ID: ${student.id}`,
        <span className={classes.status}>
            Статус:
            <span className={student.is_archive ? classes.archived : classes.active}>
                {student.is_archive ? 'В архиве' : 'Активен'}
            </span>
        </span>,
        student.is_archive && hasText(student.archive_comment)
            ? `Причина архивации: ${student.archive_comment}`
            : null,
        hasText(`${student.last_name} ${student.first_name} ${student.middle_name}`.trim())
            ? `ФИО: ${student.last_name} ${student.first_name} ${student.middle_name}`.trim()
            : null,
        hasValidDate(student.created_at)
            ? `Дата регистрации: ${student.created_at}`
            : null,
        hasText(student.phone)
            ? `Телефон: ${student.phone}`
            : null,
        hasVisibleMoney(student.cost_per_hour)
            ? `Стоимость часа: ${student.cost_per_hour} ₽`
            : null,
        student.tg?.trim()
            ?
        <a
            target="_blank"
            rel="noreferrer"
            href={student.tg}
            className={classes.highlight}
        >
            Написать в тг
        </a>
            :
        null,
        hasText(student.tutor_name) && tutorLink && canReadTutor
            ? <span>Исполнитель: <Link to={tutorLink} className={classes.highlight}>{student.tutor_name}</Link></span>
            : hasText(student.tutor_name)
                ? `Исполнитель: ${student.tutor_name}`
                : null,
        <SanctionSequenceReset
            value={student.sanction_sequence_number}
            canEdit={canResetSanctionSequence}
            onUpdate={(value) => studentService.updateSanctionSequence(student.id, value)}
        />,
    ] as (string | React.ReactNode | null)[]).filter(item => item !== null) as (string | React.ReactNode)[]

    const parentDataList = ([
        `Рабочий ТГ: ${hasText(student.work_tg_name) ? student.work_tg_name : 'Не назначен'}`,
        hasText(student.parent_full_name)
            ? `ФИО: ${student.parent_full_name}`
            : null,
        hasText(student.parent_phone)
            ? `Телефон: ${student.parent_phone}`
            : null,
        student.parent_tg?.trim()
            ?
        <a
            target="_blank"
            rel="noreferrer"
            href={student.parent_tg}
            className={classes.highlight}
        >
            Написать в тг
        </a>
            :
        null,
        Boolean(student.tg_id)
            ? `TgID: ${student.tg_id}`
            : null
    ] as (string | React.ReactNode | null)[]).filter(item => item !== null) as (string | React.ReactNode)[]

    return (
        <section className={classes.card + (hideStudentData ? ` ${classes.compact}` : '')}>
            {
                !hideStudentData && studentDataList.length > 0 &&
                <section className={classes.studentData}>
                    <DataList
                        title={hideTitles ? undefined : "Данные студента"}
                        list={studentDataList}
                    />
                </section>
            }
            {
                parentDataList.length > 0 &&
                <DataList
                    className={classes.parentData}
                    title={hideTitles ? undefined : "Данные родителя"}
                    list={parentDataList}
                />
            }
            {
                financeList.length > 0 &&
                <DataList
                    title={hideTitles ? undefined : "Финансы"}
                    list={financeList}
                />
            }
        </section>
    )
}
